import { useCallback, useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import { lockScroll, unlockScroll } from "../lib/scrollLock";

/**
 * Visionneuse plein écran avec zoom (molette, pincement tactile, double-clic)
 * et déplacement par glisser. Utilisée pour les photos produits de tous les catalogues.
 */
export default function ImageLightbox({ src, alt, onClose, testid = "image-lightbox", labels = {} }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const boxRef = useRef(null);

  // Visionneuse ouverte : gèle le défilement de la page (Lenis + CSS)
  useEffect(() => {
    lockScroll();
    return () => unlockScroll();
  }, []);
  const pinch = useRef(null);

  const clampPos = useCallback((p, s) => {
    const box = boxRef.current;
    if (!box) return p;
    const r = box.getBoundingClientRect();
    const mx = Math.max(0, (r.width * s - r.width) / 2);
    const my = Math.max(0, (r.height * s - r.height) / 2);
    return { x: Math.min(mx, Math.max(-mx, p.x)), y: Math.min(my, Math.max(-my, p.y)) };
  }, []);

  const zoomTo = useCallback((next) => {
    const s = Math.min(6, Math.max(1, next));
    setScale(s);
    setPos((p) => (s === 1 ? { x: 0, y: 0 } : clampPos(p, s)));
  }, [clampPos]);

  // molette : zoom centré
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const onWheel = (e) => {
      e.preventDefault();
      zoomTo(scale * (e.deltaY < 0 ? 1.25 : 0.8));
    };
    box.addEventListener("wheel", onWheel, { passive: false });
    return () => box.removeEventListener("wheel", onWheel);
  }, [scale, zoomTo]);

  // clavier : ESC ferme, +/- zoom
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") zoomTo(scale * 1.25);
      if (e.key === "-") zoomTo(scale * 0.8);
      if (e.key === "0") zoomTo(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, scale, zoomTo]);

  const onPointerDown = (e) => {
    if (e.pointerType === "touch") return; // le tactile passe par les événements touch ci-dessous
    drag.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current || scale === 1) return;
    setPos(clampPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }, scale));
  };
  const onPointerUp = () => { drag.current = null; };

  // tactile : 1 doigt = déplacer, 2 doigts = pincer
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      pinch.current = { d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY), s: scale };
      drag.current = null;
    } else if (e.touches.length === 1) {
      drag.current = { x: e.touches[0].clientX - pos.x, y: e.touches[0].clientY - pos.y };
    }
  };
  const onTouchMove = (e) => {
    if (pinch.current && e.touches.length === 2) {
      e.preventDefault();
      const [a, b] = e.touches;
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      zoomTo(pinch.current.s * (d / pinch.current.d));
    } else if (drag.current && e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      setPos(clampPos({ x: e.touches[0].clientX - drag.current.x, y: e.touches[0].clientY - drag.current.y }, scale));
    }
  };
  const onTouchEnd = () => { pinch.current = null; drag.current = null; };

  const btn = "w-11 h-11 rounded-full bg-ink/80 text-gold border border-gold/40 flex items-center justify-center hover:bg-gold hover:text-ink transition-colors duration-300";

  return (
    <div data-testid={testid}
      className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-sm flex flex-col p-4 md:p-8"
      role="dialog" aria-modal="true">
      <div className="flex items-center justify-end gap-2 mb-3">
        <span className="text-ivory/70 text-xs mr-2" data-testid={`${testid}-scale`}>{Math.round(scale * 100)} %</span>
        <button type="button" onClick={() => zoomTo(scale * 1.4)} className={btn} data-testid={`${testid}-zoom-in`} aria-label={labels.zoomIn || "Zoom avant"}>
          <ZoomIn className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => zoomTo(scale * 0.7)} className={btn} data-testid={`${testid}-zoom-out`} aria-label={labels.zoomOut || "Zoom arrière"}>
          <ZoomOut className="w-5 h-5" />
        </button>
        <button type="button" onClick={() => zoomTo(1)} className={btn} data-testid={`${testid}-zoom-reset`} aria-label={labels.reset || "Taille réelle"}>
          <Maximize className="w-5 h-5" />
        </button>
        <button type="button" onClick={onClose} className={btn} data-testid={`${testid}-close`} aria-label={labels.close || "Fermer"}>
          <X className="w-5 h-5" />
        </button>
      </div>
      <div ref={boxRef}
        className={`flex-1 flex items-center justify-center overflow-hidden touch-none select-none ${scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        onDoubleClick={() => zoomTo(scale === 1 ? 2.5 : 1)}
        data-testid={`${testid}-stage`}>
        <img src={src} alt={alt} draggable="false" data-testid={`${testid}-image`}
          className="max-w-full max-h-full object-contain rounded-lg transition-transform duration-100"
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }} />
      </div>
    </div>
  );
}
